<template>
  <!--
    Warehouse documents through the shared print module.
    open(items, "doc" | "label"): fetches each document (4 at a time), then
    prints one per page in the given order (group print = several).
      doc   — the document itself (items, counts, both sides, signatures)
      label — shipping label: sender + receiver name, phone, address
  -->
  <div>
    <v-dialog :value="loading" persistent max-width="320" content-class="edp-dialog">
      <div class="edp-loading" dir="rtl">
        <v-progress-circular indeterminate color="primary" size="22" width="2" />
        <span>{{ $t("entrepotPrintLoading", { n: faNum(done), m: faNum(total) }) }}</span>
      </div>
    </v-dialog>
    <print-studio
      v-if="docs.length"
      v-model="studio"
      :report="mode === 'label' ? 'entrepot-label' : 'entrepot-' + type"
      :title="title"
      :columns="columns"
      :source="source"
      :built-ins="builtIns"
      :prefer-template="mode === 'label' ? 'lbl-100x150' : 'wh-a4'"
      :extra-ctx="docs.length ? ctxOf(docs[0]) : null"
      :options="optionDefs"
      :batch="batch"
      @error="$emit('error', $event)"
    />
  </div>
</template>

<script>
import PrintStudio from "@/modules/print/PrintStudio.vue";
import { builtInWarehouseTemplates, builtInLabelTemplates } from "@/modules/print/templates";

export default {
  name: "EntrepotDocPrint",
  components: { PrintStudio },
  props: {
    // receipt | remittance | tempReceipt | useRemittance | transferRemittance …
    type: { type: String, default: "" },
  },
  data: () => ({
    studio: false,
    mode: "doc",
    docs: [],
    loading: false,
    done: 0,
    total: 0,
  }),
  computed: {
    title() {
      return this.mode === "label" ? this.$t("entrepotLabelTitle") : this.$t(this.type || "entrepot");
    },
    builtIns() {
      return this.mode === "label" ? builtInLabelTemplates : builtInWarehouseTemplates;
    },
    optionDefs() {
      if (this.mode === "label") {
        return [
          { key: "senderAddress", label: this.$t("entrepotOptSenderAddress"), default: true },
          { key: "invoiceRef", label: this.$t("entrepotOptInvoiceRef"), default: true },
        ];
      }
      return [
        { key: "productCode", label: this.$t("showProductCode") },
        { key: "articleContent", label: this.$t("showArticleContent") },
        { key: "address", label: this.$t("entrepotOptAddress"), default: true },
      ];
    },
    columns() {
      var app = this;
      return [
        {
          key: "product",
          label: this.$t("product"),
          width: 3.4,
          value: function (r) { return r.product && r.product.name ? String(r.product.name).trim() : ""; },
          sub: function (r, o) {
            var out = [];
            if (o.productCode && r.product && r.product.code != null && r.product.code !== "") {
              out.push(app.$t("product-code") + ": " + r.product.code);
            }
            if (o.articleContent && r.content && r.content !== "0") out.push(r.content);
            return out;
          },
        },
        { key: "unit", role: "unit", label: this.$t("unit"), width: 1, value: function (r) { return r.unit ? r.unit.fame || r.unit.name || "" : ""; } },
        { key: "count", role: "qty", label: this.$t("count"), format: "number", width: 1, sum: true, value: function (r) { return parseFloat(r.count) || 0; } },
      ];
    },
    batch() {
      var app = this;
      return this.docs.map(function (d) {
        return { rows: app.mode === "label" ? [] : d.articles || [], ctx: app.ctxOf(d) };
      });
    },
    source() {
      var app = this;
      var all = function () {
        return [].concat.apply([], app.batch.map(function (b) { return b.rows; }));
      };
      return {
        currentRows: all,
        total: function () { return all().length; },
        fetchPage: function () {
          return Promise.resolve({ rows: all(), lastPage: 1, total: all().length });
        },
      };
    },
  },
  methods: {
    faNum(v) {
      var s = String(v == null ? "" : v);
      return String(this.$i18n && this.$i18n.locale).indexOf("fa") === 0
        ? s.replace(/[0-9]/g, function (d) { return "۰۱۲۳۴۵۶۷۸۹"[d]; })
        : s;
    },
    open(items, mode) {
      var app = this;
      var list = (items || []).filter(Boolean);
      if (!list.length) return;
      app.mode = mode === "label" ? "label" : "doc";
      app.studio = false;
      app.docs = [];
      app.loading = true;
      app.done = 0;
      app.total = list.length;
      var out = new Array(list.length);
      var next = 0;
      var worker = function () {
        if (next >= list.length) return Promise.resolve();
        var i = next++;
        return app.$axios
          .get(app.$endpoint + "entrepot/" + list[i].id + "/edit", { params: { id: list[i].id, print: "active" } })
          .then(function (res) {
            out[i] = res.data;
            app.done++;
          })
          .then(worker);
      };
      Promise.all([worker(), worker(), worker(), worker()])
        .then(function () {
          return app.loadReceivers(out.filter(Boolean));
        })
        .then(function (docs) {
          app.docs = docs;
          app.$nextTick(function () {
            app.studio = true;
          });
        })
        .catch(function (err) {
          app.$emit("error", err);
        })
        .finally(function () {
          app.loading = false;
        });
    },
    // The customer inside a warehouse document is a short record (no address),
    // so the full one is fetched once per customer.
    loadReceivers(docs) {
      var app = this;
      var ids = {};
      docs.forEach(function (d) {
        var c = d.invoice && d.invoice.customer;
        if (c && c.id && !c.address) ids[c.id] = true;
      });
      var cache = {};
      return Promise.all(
        Object.keys(ids).map(function (id) {
          return app.$axios
            .get(app.$endpoint + "customer/" + id + "/edit")
            .then(function (res) {
              cache[id] = res.data || null;
            })
            .catch(function () {
              cache[id] = null; // print without the address rather than fail
            });
        })
      ).then(function () {
        docs.forEach(function (d) {
          var c = d.invoice && d.invoice.customer;
          var full = c && cache[c.id];
          if (full) d.invoice.customer = Object.assign({}, c, full);
        });
        return docs;
      });
    },
    // "state, city, address"
    fullAddress(c) {
      var name = function (x) {
        return x && typeof x === "object" ? x.name || x.title || "" : x || "";
      };
      return [name(c.state), name(c.city), c.address].filter(function (x) { return x && x !== "null"; }).join("، ");
    },
    filled(l) {
      return l.value != null && l.value !== "" && l.value !== "null";
    },
    // Receiver: the invoice's customer (remittance to a customer) or the
    // destination warehouse (transfer).
    receiverOf(d) {
      var c = (d.invoice && d.invoice.customer) || d.customer || d.account || null;
      if (c) {
        return {
          name: c.name,
          mobile: c.mobile,
          phone: c.phone,
          address: this.fullAddress(c),
          postal: c.postal_code || c.postalCode,
        };
      }
      if (d.recipient_entrepot) return { name: d.recipient_entrepot.name, address: d.recipient_entrepot.address };
      return {};
    },
    ctxOf(d) {
      var self = d.self || {};
      var s = this.$store.state;
      var rc = this.receiverOf(d);
      var invoiceName = d.invoice && d.invoice.name ? d.invoice.name : "";
      var ctx;
      if (this.mode === "label") {
        ctx = {
          title: "",
          parties: {
            seller: {
              title: this.$t("entrepotSender"),
              lines: [
                { label: this.$t("name"), value: self.abrName || s.abrName },
                { label: this.$t("phone"), value: self.phone || self.mobile },
                { label: this.$t("address"), value: self.address, option: "senderAddress" },
              ].filter(this.filled),
            },
            buyer: {
              title: this.$t("entrepotReceiver"),
              lines: [
                { label: this.$t("name"), value: rc.name },
                { label: this.$t("mobile"), value: rc.mobile },
                { label: this.$t("phone"), value: rc.phone },
                { label: this.$t("address"), value: rc.address },
                { label: this.$t("postal-code"), value: rc.postal },
              ].filter(this.filled),
            },
          },
          info: [
            { label: this.$t("number"), value: d.id },
            { label: this.$t("date"), value: d.date },
            { label: this.$t("relative-document"), value: invoiceName, option: "invoiceRef" },
          ],
        };
      } else {
        ctx = {
          title: this.$t(d.type || this.type || "entrepot"),
          parties: {
            seller: {
              title: this.$t("originWarehouse"),
              lines: [{ label: this.$t("name"), value: d.entrepot && d.entrepot.name }].filter(this.filled),
            },
            buyer: {
              title: this.$t(d.recipient_entrepot ? "destinationWarehouse" : "entrepotReceiver"),
              lines: [
                { label: this.$t("name"), value: rc.name },
                { label: this.$t("mobile"), value: rc.mobile || rc.phone },
                { label: this.$t("address"), value: rc.address, option: "address" },
              ].filter(this.filled),
            },
          },
          info: [
            { label: this.$t("number"), value: d.id },
            { label: this.$t("date"), value: d.date },
            { label: this.$t("relative-document"), value: invoiceName },
          ],
          itemsLabel: this.$t("product"),
          signatureLabels: [this.$t("delivererSignature"), this.$t("recipientSignature")],
        };
      }
      if (self.abrName) ctx.company = self.abrName;
      if (self.logo && self.logo.link) ctx.logo = self.logo.link;
      return ctx;
    },
  },
};
</script>

<style scoped>
::v-deep .edp-dialog {
  border-radius: 6px;
}
.edp-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: #fff;
  border-radius: 6px;
  font-size: 0.85rem;
}
.theme--dark .edp-loading {
  background: #1e1e1e;
}
</style>
